import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/auth";
import NexWordmark from "@/components/NexWordmark";
import { LIFE_LOGO } from "@/lib/assets";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirmPw) return toast.error("Passwords don't match");
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — you can sign in now.");
      setTimeout(() => navigate("/admin/login"), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
      <div className="absolute inset-0 blueprint-bg opacity-40 pointer-events-none" />
      <div className="w-full max-w-md relative">
        <Link to="/" className="flex items-center justify-center gap-4 mb-10">
          <img src={LIFE_LOGO} alt="LIFE" className="h-14 w-14 object-contain" />
          <div className="w-px h-12 bg-nex-border" />
          <div>
            <NexWordmark size="text-3xl" />
            <div className="mono-body text-[10px] tracking-[0.28em] uppercase mt-1">Owner Console</div>
          </div>
        </Link>

        {!token ? (
          <div className="card-industrial corner-tick p-8 text-center">
            <div className="text-nex-gold mono-label mb-3">// ERROR</div>
            <p className="text-nex-chrome mb-6">Invalid reset link — no token provided.</p>
            <Link to="/admin/forgot-password" className="tick-btn-secondary inline-flex">Request a new one</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="card-industrial corner-tick p-8 md:p-10 text-center">
            <CheckCircle2 size={40} className="text-nex-gold mx-auto mb-4" />
            <h1 className="serif-head text-2xl mb-3">Password updated</h1>
            <p className="text-nex-chrome">Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="card-industrial corner-tick p-8 md:p-10 space-y-6">
            <div className="text-center">
              <Lock size={22} className="text-nex-gold mx-auto mb-3" />
              <div className="mono-label mb-2">// SET NEW PASSWORD</div>
              <h1 className="serif-head text-3xl">Reset password</h1>
            </div>

            <div>
              <label className="mono-label block mb-2">New password</label>
              <input
                data-testid="reset-password"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="input-industrial"
                minLength={8}
                required
                autoFocus
              />
              <div className="mono-body text-[10px] text-nex-muted mt-2">At least 8 characters.</div>
            </div>
            <div>
              <label className="mono-label block mb-2">Confirm new password</label>
              <input
                data-testid="reset-password-confirm"
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="input-industrial"
                required
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="tick-btn-primary w-full justify-center disabled:opacity-50"
            >
              {busy ? <><Loader2 size={16} className="animate-spin" /> Updating...</> : <>Update Password <Lock size={16} /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
